import { MiniGameType } from '../interfaces/mini-game.interface';

export interface GameInfoSection {
  titleEn: string;
  titleAr: string;
  items: { en: string; ar: string }[];
}

export interface GameInfoContent {
  howToPlayEn: string;
  howToPlayAr: string;
  sections: GameInfoSection[];
}

export const GAME_INFO: Record<string, GameInfoContent> = {
  [MiniGameType.SPIN_WHEEL]: {
    howToPlayEn: 'Spin the wheel to randomly select a student. The chosen student must answer the question before time runs out!',
    howToPlayAr: 'أدر العجلة لاختيار طالب عشوائياً. يجب على الطالب المختار الإجابة قبل انتهاء الوقت!',
    sections: [
      {
        titleEn: 'Scoring',
        titleAr: 'النقاط',
        items: [
          { en: '1.25x multiplier on all correct answers', ar: 'مضاعف ١٫٢٥x على جميع الإجابات الصحيحة' },
          { en: '7 questions, 30 seconds each', ar: '٧ أسئلة، ٣٠ ثانية لكل سؤال' },
          { en: 'Base XP: Easy = 10, Medium = 20, Hard = 35', ar: 'نقاط أساسية: سهل = ١٠، متوسط = ٢٠، صعب = ٣٥' },
        ]
      },
      {
        titleEn: 'Bonuses',
        titleAr: 'المكافآت',
        items: [
          { en: 'Speed bonus: +5 XP (>75% time left), +3 (>50%), +1 (>25%)', ar: 'مكافأة السرعة: +٥ (أكثر من ٧٥% وقت متبقي)، +٣ (>٥٠%)، +١ (>٢٥%)' },
          { en: 'Streak multiplier: up to 2.5x for 20+ correct in a row', ar: 'مضاعف السلسلة: حتى ٢٫٥x لـ +٢٠ إجابة صحيحة متتالية' },
          { en: 'Daily subject bonus: 1.5x XP for the daily featured subject', ar: 'مكافأة المادة اليومية: ١٫٥x نقاط للمادة المميزة اليوم' },
        ]
      }
    ]
  },

  [MiniGameType.TUG_OF_WAR]: {
    howToPlayEn: 'Two teams compete simultaneously! Both teams answer questions at the same time. Correct answers pull the rope toward your side.',
    howToPlayAr: 'فريقان يتنافسان في نفس الوقت! كلا الفريقين يجيبان على الأسئلة معاً. الإجابات الصحيحة تسحب الحبل نحو جهتك.',
    sections: [
      {
        titleEn: 'Scoring',
        titleAr: 'النقاط',
        items: [
          { en: '10 rounds, 25 seconds per team each round', ar: '١٠ جولات، ٢٥ ثانية لكل فريق في كل جولة' },
          { en: 'Correct answer: Pull rope +10 units toward your side', ar: 'إجابة صحيحة: سحب الحبل +١٠ وحدات نحو جهتك' },
          { en: 'Wrong answer: Rope slips -3 units (opponent gains)', ar: 'إجابة خاطئة: الحبل ينزلق -٣ وحدات (الخصم يستفيد)' },
        ]
      },
      {
        titleEn: 'Bonuses',
        titleAr: 'المكافآت',
        items: [
          { en: 'Speed bonus: answer faster to earn extra XP', ar: 'مكافأة السرعة: أجب أسرع لكسب نقاط إضافية' },
          { en: 'Streak bonus: consecutive correct answers multiply XP', ar: 'مكافأة السلسلة: الإجابات الصحيحة المتتالية تضاعف النقاط' },
          { en: 'Winner team gets the final rope position advantage', ar: 'الفريق الفائز يحصل على ميزة موقع الحبل النهائي' },
        ]
      }
    ]
  },

  [MiniGameType.LIGHTNING_ROUND]: {
    howToPlayEn: 'Answer as fast as you can! Questions auto-submit when you select an option. Build combos for massive multipliers!',
    howToPlayAr: 'أجب بأسرع ما يمكن! الأسئلة تُرسل تلقائياً عند اختيار الإجابة. ابنِ سلسلة للحصول على مضاعفات ضخمة!',
    sections: [
      {
        titleEn: 'Scoring',
        titleAr: 'النقاط',
        items: [
          { en: '12 questions, only 10 seconds each!', ar: '١٢ سؤال، ١٠ ثوانٍ فقط لكل سؤال!' },
          { en: 'Auto-submit: selecting an option immediately submits', ar: 'إرسال تلقائي: اختيار الإجابة يرسلها فوراً' },
          { en: 'Wrong answer resets your combo to zero', ar: 'الإجابة الخاطئة تعيد السلسلة إلى الصفر' },
        ]
      },
      {
        titleEn: 'Combo Multipliers',
        titleAr: 'مضاعفات السلسلة',
        items: [
          { en: '0-1 correct in a row: 1x (no bonus)', ar: '٠-١ إجابة صحيحة متتالية: ١x (بدون مكافأة)' },
          { en: '2-3 correct in a row: 1.5x multiplier', ar: '٢-٣ إجابات صحيحة متتالية: مضاعف ١٫٥x' },
          { en: '4-5 correct in a row: 2x multiplier', ar: '٤-٥ إجابات صحيحة متتالية: مضاعف ٢x' },
          { en: '6+ correct in a row: 3x multiplier!', ar: '+٦ إجابات صحيحة متتالية: مضاعف ٣x!' },
        ]
      }
    ]
  },

  [MiniGameType.BOMB_PASS]: {
    howToPlayEn: 'Hot potato! A bomb passes between students. Answer correctly to pass the bomb. If the fuse runs out while you hold it... BOOM!',
    howToPlayAr: 'البطاطا الساخنة! قنبلة تتنقل بين الطلاب. أجب بشكل صحيح لتمرير القنبلة. إذا انتهى الفتيل وأنت تحملها... بوم!',
    sections: [
      {
        titleEn: 'Scoring',
        titleAr: 'النقاط',
        items: [
          { en: '10 questions with a random fuse timer (8-15 seconds)', ar: '١٠ أسئلة مع مؤقت فتيل عشوائي (٨-١٥ ثانية)' },
          { en: 'Correct answer: earn XP and pass the bomb safely', ar: 'إجابة صحيحة: اكسب نقاط ومرر القنبلة بأمان' },
          { en: 'Wrong answer or timeout: the bomb explodes on you!', ar: 'إجابة خاطئة أو انتهاء الوقت: القنبلة تنفجر عليك!' },
        ]
      },
      {
        titleEn: 'Bonuses',
        titleAr: 'المكافآت',
        items: [
          { en: 'Hot Potato bonus: +5 XP if correct with 3 seconds or less left', ar: 'مكافأة البطاطا الساخنة: +٥ نقاط إذا أجبت صح مع ٣ ثوانٍ أو أقل متبقية' },
          { en: 'Speed bonus applies on all correct answers', ar: 'مكافأة السرعة تنطبق على جميع الإجابات الصحيحة' },
          { en: 'Streak multiplier for consecutive correct answers', ar: 'مضاعف السلسلة للإجابات الصحيحة المتتالية' },
        ]
      }
    ]
  },

  [MiniGameType.MYSTERY_BOX]: {
    howToPlayEn: 'Pick a mystery box to reveal a modifier card — it could be a bonus or a bamboozle! Then answer the question with the modifier applied.',
    howToPlayAr: 'اختر صندوقاً غامضاً لكشف بطاقة معدِّلة — قد تكون مكافأة أو مقلب! ثم أجب على السؤال مع تطبيق المعدِّل.',
    sections: [
      {
        titleEn: 'Scoring',
        titleAr: 'النقاط',
        items: [
          { en: '8 questions, 12 mystery boxes to pick from', ar: '٨ أسئلة، ١٢ صندوقاً غامضاً للاختيار منها' },
          { en: 'Default time: 30 seconds (modified by cards)', ar: 'الوقت الافتراضي: ٣٠ ثانية (يتغير بالبطاقات)' },
          { en: 'Timer expiry counts as a wrong answer', ar: 'انتهاء المؤقت يُحسب كإجابة خاطئة' },
        ]
      },
      {
        titleEn: 'Bonus Cards',
        titleAr: 'بطاقات المكافأة',
        items: [
          { en: 'Double XP: Earn 2x XP this round', ar: 'نقاط مضاعفة: اكسب ٢x نقاط في هذه الجولة' },
          { en: 'Triple XP: Earn 3x XP this round', ar: 'نقاط ثلاثية: اكسب ٣x نقاط في هذه الجولة' },
          { en: 'Free Pass: Get 5 XP even if wrong', ar: 'تمريرة مجانية: احصل على ٥ نقاط حتى لو أخطأت' },
          { en: 'Extra Time: 45 seconds to answer', ar: 'وقت إضافي: ٤٥ ثانية للإجابة' },
          { en: 'Shield: No penalty if wrong', ar: 'درع: لا خسارة إذا أخطأت' },
        ]
      },
      {
        titleEn: 'Bamboozle Cards',
        titleAr: 'بطاقات المقالب',
        items: [
          { en: 'Half Time: Only 15 seconds!', ar: 'نصف الوقت: ١٥ ثانية فقط!' },
          { en: 'Brain Freeze: Only 10 seconds!', ar: 'تجمد الدماغ: ١٠ ثوانٍ فقط!' },
          { en: 'Steal or Lose: 2x if right, -10 if wrong', ar: 'اسرق أو اخسر: ٢x إذا صح، -١٠ إذا خطأ' },
          { en: 'Score Drain: -15 XP penalty', ar: 'استنزاف النقاط: -١٥ نقطة' },
          { en: 'Reverse: Right = -5, Wrong = +5!', ar: 'عكس: صح = -٥، خطأ = +٥!' },
        ]
      }
    ]
  }
};
